import { Check, Copy, Edit2, Trash2 } from "lucide-react"
import { formatDateTime } from "../../../../utils/datetime";
import { useEffect, useMemo, useState } from 'react'
import PageSizeSelect from "../../../../components/PageSizeSelect"

const PAGE_WINDOW = 3

const IntentTable = ({
  intents,
  selectedIds,
  onToggleAll,
  onToggleOne,
  onEdit,
  onDelete,
  onDuplicate,
  onApprove,
  canEdit = true,
  canDelete = true,
}) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    setCurrentPage(1)
  }, [intents.length])

  const totalPages = Math.max(1, Math.ceil(intents.length / pageSize))

  const paginatedIntents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return intents.slice(startIndex, startIndex + pageSize)
  }, [intents, currentPage, pageSize])

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const getPageNumbers = () => {
    let start = Math.max(1, currentPage - 1)
    let end = start + PAGE_WINDOW - 1

    if (end > totalPages) {
      end = totalPages
      start = Math.max(1, end - PAGE_WINDOW + 1)
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  return (
    <div className="bg-white rounded-4 shadow d-flex flex-column h-100">

      {/* TABLE */}
      <div className="table-responsive flex-grow-1">
        <table className="table table-hover mb-0">
          <thead className="table-light sticky-top">
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={intents.length > 0 && selectedIds.length === intents.length}
                  onChange={onToggleAll}
                />
              </th>

              <th style={{ width: 56 }}>S.No</th>
              <th>Intent Name</th>
              <th>Display Name</th>

              <th>Domain</th>
              <th className="col-category">Category</th>
              <th className="col-phrases">Phrases</th>
              <th className="col-responses">Responses</th>

              <th>Usage (30d)</th>
              <th className="col-confidence">Confidence</th>
              <th>Status</th>
              <th className="col-lastModified">Last Modified</th>

              <th className="text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginatedIntents.map((intent, i) => (
              <tr key={intent.id}>

                <td>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedIds.includes(intent.id)}
                    onChange={() => onToggleOne(intent.id)}
                  />
                </td>

                <td className="text-muted">{(currentPage - 1) * pageSize + i + 1}</td>

                <td
                  className="text-primary fw-bold cursor-pointer"
                  onClick={() => onEdit(intent)}
                >
                  {intent.intent_name}
                </td>

                <td>{intent.name}</td>

                <td>
                  <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                    {intent.intent_domain_name || "—"}
                  </span>
                </td>

                <td className="col-category">
                  <span className="badge badge-category">
                    {intent.category_name}
                  </span>
                </td>

                <td className="col-phrases">
                  <span className={`badge ${
                    intent.phrases_count < 3 ? "bg-danger" :
                    intent.phrases_count < 10 ? "bg-warning text-dark" : "bg-success"
                  }`}>
                    {intent.phrases_count} phrases
                  </span>
                </td>
                <td className="col-responses">{intent.responses_count}</td>

                <td>{intent.usage}</td>
                <td className="col-confidence">{intent.confidence}%</td>

                <td>
                  <span
                    className={`badge ${intent.status === "ACTIVE"
                      ? "bg-success"
                      : "bg-danger"
                      }`}
                  >
                    {intent.status}
                  </span>
                  {intent.approval_status && intent.approval_status !== "APPROVED" && (
                    <span
                      className="badge bg-warning text-dark d-block mt-1"
                      title="Not yet included in training — approve to add it to the model"
                    >
                      Pending approval
                    </span>
                  )}
                </td>

                <td className="col-lastModified">
                  {formatDateTime(intent.last_modified)}
                </td>

                <td className="text-end">
                  <div className="d-flex gap-3 justify-content-end">
                    {canEdit && onApprove && intent.approval_status && intent.approval_status !== "APPROVED" && (
                      <Check
                        size={16}
                        className="cursorPointer text-success"
                        aria-label="Approve intent"
                        title="Approve this intent so it's included in the next training run"
                        onClick={() => onApprove(intent)}
                      />
                    )}
                    {canEdit && (
                      <Edit2
                        size={16}
                        className="cursorPointer"
                        onClick={() => onEdit(intent)}
                      />
                    )}
                    {canEdit && (
                      <Copy
                        size={16}
                        className="cursorPointer"
                        onClick={() => onDuplicate?.(intent)}
                      />
                    )}
                    {canDelete && (
                      <Trash2
                        size={16}
                        className="cursorPointer text-danger"
                        onClick={() => onDelete(intent)}
                      />
                    )}
                  </div>
                </td>
              </tr>
            ))}

            {intents.length === 0 && (
              <tr>
                <td colSpan={13} className="text-center py-4 text-muted">
                  No intents found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION + rows-per-page */}
      <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top flex-wrap gap-2">
        <div className="d-flex align-items-center gap-3">
          <PageSizeSelect
            value={pageSize}
            onChange={(n) => { setPageSize(n); setCurrentPage(1); }}
          />
          <small className="text-muted">
            Page {currentPage} of {totalPages}
          </small>
        </div>

        {totalPages > 1 && (
          <nav className="custom-pagination">
            <ul className="pagination pagination-sm mb-0 align-items-center">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link pill prev"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Prev
                </button>
              </li>

              {getPageNumbers().map(page => (
                <li
                  key={page}
                  className={`page-item ${currentPage === page ? 'active' : ''}`}
                >
                  <button
                    className="page-link pill"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                </li>
              ))}

              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link pill next"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </div>
  )
}

export default IntentTable
